import { Dispatch } from "react-redux";
import type {
    CustomizationImageFilterDictionary,
    IBulkCustomizationQuery,
    ICreateProofActionPayload,
    IImageUpdateData,
    IProductDataEntry,
    IUploadPayload,
    IUploadProofBody,
} from "constants/product-customizations/customization";
import { TCustomizationSelection } from "constants/product-customizations/customization-selection.types";
import { CUSTOMIZATION } from "constants/action-types";
import type { IAction, IFetchParams } from "epics/base-epic";
import type { IStore } from "reducers";
import type { ICustomizationImageEntity } from "epics/customizations-epic";
import type { IProduct } from "records/item/item";
import {
    CustomizationImageStatus,
    IBrandSettingsTypesUpdateData,
} from "constants/product-customizations/customization";
import { ThunkAction } from "redux-thunk";
import { IEditItemPayload } from "../reducers/customization-reducers";

const IMAGE_PAGE_SIZE = 10;

export interface IFetchLogoParams {
    merchantId: string;
    status?: string;
    search?: string;
    filters?: CustomizationImageFilterDictionary;
}

export const fetchProductTemplate = (templateId: string) => {
    return {
        type: CUSTOMIZATION.PRODUCT_TEMPLATES.FETCH.START,
        payload: { url: `/api/v3.0/product-customization/product-templates/${templateId}`, templateId },
    };
};

export const resetCurrentSelection = () => {
    return {
        type: CUSTOMIZATION.SET_SELECTION,
        payload: {
            customizations: [],
            templateId: "",
        },
    };
};

export const resetBrandSettingsUpdateError = () => {
    return {
        type: CUSTOMIZATION.BRAND_SETTINGS.RESET_ERROR,
        payload: null,
    };
};

export const resetProductTemplate = () => (dispatch: Dispatch<{}>) => {
    return [
        dispatch(resetCurrentSelection()),
        dispatch({
            type: CUSTOMIZATION.PRODUCT_TEMPLATES.FETCH.SUCCESS,
            payload: null,
        }),
    ];
};

export const setCustomizationSelection = (customizations: TCustomizationSelection[] = [], templateId = "") => {
    return {
        type: CUSTOMIZATION.SET_SELECTION,
        payload: {
            customizations,
            templateId,
        },
    };
};

export const resetLineItemEdit = () => {
    return {
        type: CUSTOMIZATION.EDIT_LINE_ITEM,
        payload: {
            itemId: "",
            customizations: [],
        },
    };
};

export const setLineItemEdit = (payload: IEditItemPayload) => {
    return {
        type: CUSTOMIZATION.EDIT_LINE_ITEM,
        payload,
    };
};

export const resetProofWarning = () => {
    return {
        type: CUSTOMIZATION.PROOF_WARNING_MODAL,
        payload: {
            itemId: "",
            customizations: [],
        },
    };
};

export const resetBulkCustomizationModal = () => {
    return {
        type: CUSTOMIZATION.BULK_CUSTOMIZATIONS_MODAL,
        payload: {
            product: null,
            customizations: [],
            productDataEntries: [],
            templateId: "",
            payload: [],
            isFetchingProducts: false,
        },
    };
};

export const showBulkCustomizationModal = (
    product: IProduct,
    customizations: TCustomizationSelection[],
    productDataEntries: IProductDataEntry[]
) => {
    return {
        type: CUSTOMIZATION.BULK_CUSTOMIZATIONS_MODAL,
        payload: {
            product,
            customizations,
            productDataEntries,
        },
    };
};

export const createCustomizationImage = ({
    uploadId,
    filename,
    mimetype,
    forMerchantIds = [],
}: {
    uploadId: string;
    filename: string;
    mimetype: string;
    forMerchantIds?: string[];
}) => {
    return {
        type: CUSTOMIZATION.CREATE_CUSTOMIZATION_IMAGE.CREATE.START,
        payload: {
            url: "/api/v3.0/product-customization/customization-images",
            body: {
                name: filename || "customization-image",
                kind: getKind(mimetype, filename),
                uploadId,
                additionalUploads: [],
                forMerchantIds,
            },
        },
    };
};

export const resetUploadedImage = () => {
    return {
        type: CUSTOMIZATION.CREATE_CUSTOMIZATION_IMAGE.CLEAR,
    };
};

export const resetBulkCustomizationProducts = () => {
    return {
        type: CUSTOMIZATION.BULK_CUSTOMIZATIONS.PRODUCTS.CLEAR,
    };
};

export const fetchImageLogos =
    ({ merchantId, status, search, filters }: IFetchLogoParams): ThunkAction<void, IStore, IAction<IFetchParams>> =>
    (dispatch: Dispatch<IStore>, getState: () => IStore) => {
        const { nextPageToken } = getState().customization.logosManagement.payload;
        dispatch({
            type: CUSTOMIZATION.LIST_MANAGE_LOGOS.FETCH.START,
            payload: {
                url: "/api/v3.0/product-customization/customization-images/filter?__populate=merchants,download-urls",
                body: {
                    limit: IMAGE_PAGE_SIZE,
                    ...(search && { search }),
                    ...(nextPageToken && { lastId: nextPageToken }),
                    ...(merchantId && { forMerchantId: merchantId }),
                    ...(status && { status }),
                    ...(filters && { filters }),
                },
            },
        });
    };

export const fetchExistingCustomizationImages =
    (
        forMerchantId?: string,
        search?: string,
        filters?: CustomizationImageFilterDictionary,
        status?: CustomizationImageStatus
    ) =>
    (dispatch: Dispatch<IAction<IFetchParams>>, getState: () => IStore) => {
        const { nextPageToken } = getState().customization.existingImages.payload;
        dispatch({
            type: CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.FETCH.START,
            payload: {
                url: "/api/v3.0/product-customization/customization-images/filter",
                body: {
                    limit: IMAGE_PAGE_SIZE,
                    ...(search && { search }),
                    ...(nextPageToken && { lastId: nextPageToken }),
                    ...(forMerchantId && { forMerchantId }),
                    ...(status && { status }),
                    ...(filters && { filters }),
                },
            },
        });
    };

export const checkCustomizationImages =
    (forMerchantId?: string, search?: string, filters?: CustomizationImageFilterDictionary, ids?: string[]) =>
    (dispatch: Dispatch<IAction<IFetchParams>>) => {
        dispatch({
            type: CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.CHECK.START,
            payload: {
                url: "/api/v3.0/product-customization/customization-images/filter",
                body: {
                    limit: IMAGE_PAGE_SIZE,
                    ...(search && { search }),
                    ...(forMerchantId && { forMerchantId }),
                    ...(filters && { filters }),
                    ...(ids && { ids }),
                },
            },
        });
    };

export const fetchCustomizationImageDownloadURL =
    (imageId: string) => (dispatch: Dispatch<IStore>, getState: () => IStore) => {
        dispatch({
            type: CUSTOMIZATION.GET_CUSTOMIZATION_IMAGE_DOWNLOAD_URL.FETCH.START,
            payload: {
                imageId: imageId,
                url: `/api/v3.0/product-customization/customization-images/download-url/${imageId}`,
            },
        });
    };

export const resetImageUrls = () => {
    return { type: CUSTOMIZATION.GET_CUSTOMIZATION_IMAGE_DOWNLOAD_URL.RESET };
};

export const cancelFetchCustomizationImages = () => (dispatch: Dispatch<IAction<IFetchParams>>) => {
    dispatch({
        type: CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.FETCH.CANCEL,
    });
};

export const addImageToList =
    (image: ICustomizationImageEntity) => (dispatch: Dispatch<IAction<IFetchParams>>, getState: () => IStore) => {
        const {
            payload,
            payload: { images },
        } = getState().customization.existingImages;

        const updatedImages = [image, ...images];

        dispatch({
            type: CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.UPDATE,
            payload: {
                ...payload,
                images: updatedImages,
            },
        });
    };

export const resetExistingImagesList = () => ({ type: CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.RESET });
export const setImagesToExistingImagesList = (storedValues) => ({
    type: CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.SET_STORED_VALUES,
    payload: {
        images: storedValues.images,
        nextPageToken: storedValues.nextPageToken,
    },
});

export const setSearching = (value) => ({
    type: CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.SEARCH,
    payload: { isSearching: value },
});

export const fetchCompany = (companyId: string) => ({
    type: CUSTOMIZATION.COMPANY.FETCH.START,
    payload: { url: `/api/v3.1/company/${companyId}` },
});

const getKind = (mimetype: string, filename: string): string | undefined => {
    filename = filename.toLowerCase();
    // looks like the best we can do is look at the extension too
    if ((mimetype === "application/postscript" && filename.endsWith(".eps")) || mimetype === "image/x-eps") {
        return "EPS";
    }

    if (mimetype === "application/postscript" && filename.endsWith(".ai")) {
        return "AI";
    }

    if (mimetype === "application/pdf") {
        return "PDF";
    }

    if (mimetype === "image/svg+xml") {
        return "SVG";
    }

    if (mimetype === "image/png") {
        return "IMAGE";
    }

    if (mimetype === "image/jpeg") {
        return "JPEG";
    }

    if (mimetype === "application/octet-stream" && filename.endsWith(".dst")) {
        return "DST";
    }

    return undefined;
};

export const fetchCustomizationProductById = (id: string) => ({
    type: CUSTOMIZATION.PRODUCT.FETCH.START,
    payload: id,
});

export const resetCustomizationProduct = () => ({
    type: CUSTOMIZATION.PRODUCT.RESET,
});

export const resetOrder = () => ({
    type: CUSTOMIZATION.ORDER.RESET,
});

export const fetchBulkCustomizationProducts = (params: IBulkCustomizationQuery) => {
    return {
        type: CUSTOMIZATION.BULK_CUSTOMIZATIONS.PRODUCTS.FETCH.START,
        payload: {
            url: "/api/v3.0/product-customization/bulk-products/query",
            body: params,
        },
    };
};

export const updateBrandSettings = (params: IBrandSettingsTypesUpdateData) => {
    return {
        type: CUSTOMIZATION.BRAND_SETTINGS.UPDATE.START,
        payload: {
            url: "/brand/settings/product-customizations",
            body: params,
        },
    };
};

export const checkImagesTab =
    (forMerchantId?: string, ids?: string[]) => (dispatch: Dispatch<IAction<IFetchParams>>) => {
        dispatch({
            type: CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.CHECK_IMAGE_TAB.START,
            payload: {
                url: "/api/v3.0/product-customization/customization-images/filter",
                body: {
                    ...(forMerchantId && { forMerchantId }),
                    ...(ids && { ids }),
                },
            },
        });
    };

export const fetchBrandImageTagSchemas = () => ({
    type: CUSTOMIZATION.IMAGE_TAG_SCHEMAS.BRAND.FETCH.START,
    payload: {
        url: "/api/v3.0/product-customization/customization-image-tag-schemas",
    },
});

export const createProof = (payload: ICreateProofActionPayload) => {
    const { orderId, body, file, lineItemId } = payload;
    return {
        type: CUSTOMIZATION.PROOF.CREATE.START,
        payload: {
            url: `/api/v3.0/order/${orderId}/proof-of-product`,
            orderId,
            body,
            file,
            lineItemId,
        },
    };
};

export const resetProof = () => ({
    type: CUSTOMIZATION.PROOF.RESET,
});

export const onUploadProof = (payload: IUploadPayload) => ({
    type: CUSTOMIZATION.PROOF.UPLOAD.START,
    payload,
});

export const onAssignProof = (orderId: string, body: IUploadProofBody) => {
    return {
        type: CUSTOMIZATION.PROOF.ASSIGN.START,
        payload: {
            url: `/api/v3.0/order/${orderId}/proof-of-product`,
            body,
        },
    };
};

export const updateCustomizationImage = (imageData: IImageUpdateData) => {
    const { id } = imageData;
    return {
        type: CUSTOMIZATION.CUSTOMIZATION_IMAGE.UPDATE.START,
        payload: {
            url: `/api/v3.0/product-customization/customization-images/${id}`,
            body: imageData,
        },
    };
};

export const checkOrderCustomizations = (orderId: string) => {
    return {
        type: CUSTOMIZATION.ORDER.CHECK.START,
        payload: {
            url: `/api/v3.0/order/${orderId}/customization-setup`,
        },
    };
};

export const resetCustomizationCheckedImages = () => {
    return {
        type: CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.RESET_CHECKED,
    };
};

export const resetCheckedImageTabImages = () => ({
    type: CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.RESET_IMAGE_TAB,
});

export const resetLogos = () => ({
    type: CUSTOMIZATION.LIST_MANAGE_LOGOS.RESET,
});
